import type { AddressBookContactDetail } from "~/shared/schemas/address-book-schema.ts";

import { getAccessorialIds } from "./address-book-accessorials.ts";

type ContactDetailFields = Pick<
	AddressBookContactDetail,
	| "contactName"
	| "contactPhone"
	| "contactExtension"
	| "hoursOpen"
	| "hoursClose"
	| "specialInstructions"
	| "accessorials"
>;

const isNonEmptyString = (value?: string | null) =>
	typeof value === "string" && value.trim().length > 0;

/**
 * Determines whether a stop contact detail carries any meaningful data.
 *
 * Mirrors the blank-detail rule used when creating/editing address book
 * entries (see `getStopDetail` in address-book-address): a detail is
 * considered populated when it has a contact name, contact phone, contact
 * extension, hours open/close, special instructions, or accessorials. Use
 * this anywhere a save flow needs to decide whether to preserve a
 * non-current stop detail so the logic stays consistent across
 * address-book and quote flows.
 *
 * @param detail - The contact detail to inspect (any address-book detail shape)
 * @returns `true` if the detail has any populated field, otherwise `false`
 */
export const hasContactDetailData = (
	detail: Partial<ContactDetailFields> | undefined,
): boolean => {
	if (!detail) return false;

	return (
		isNonEmptyString(detail.contactName) ||
		isNonEmptyString(detail.contactPhone) ||
		isNonEmptyString(detail.contactExtension) ||
		isNonEmptyString(detail.hoursOpen) ||
		isNonEmptyString(detail.hoursClose) ||
		isNonEmptyString(detail.specialInstructions) ||
		getAccessorialIds(detail.accessorials).length > 0
	);
};
